import { gallons } from "./accounts";
import type { ParityDetail, ParityFlag, ParityInputs, ParityPickup, ParityReason, ParityRow, ParitySide, ParitySummary } from "./api";
import { PICKUP_SOURCE_TEXT } from "./dayPickups";
import { percentText } from "./parityHistory";
import { dateLabel } from "./format";
import type { SortColumn } from "./sort";

export const NOTE_MAX = 500;

export const REASON_TEXT: Record<ParityReason, string> = {
  sheet_wrong: "The sheet is wrong",
  app_wrong: "The app is wrong",
  timing: "Timing: a newer pickup or visit",
  new_account: "New account",
  other: "Other",
};

export const FLAG_TEXT: Record<ParityFlag, string> = {
  high_risk: "High risk",
  overflow: "Overflow",
  due: "Due",
};

/** The name of each value that can differ, as the Differences cell shows it. */
export const DIFFERENCE_TEXT: Record<string, string> = {
  rate: "rate",
  weeks_to_full: "weeks to full",
  load: "load",
  ...FLAG_TEXT,
};

/** Design 26.5 T2: the areas that count only the app pickups, each with its switch date. */
export function appAreasText(areas: readonly { code: string; app_from: string }[] | undefined): string {
  // An API before 0069 sends no app_areas.
  const listed = areas ?? [];
  return listed.length ? `On the app: ${listed.map((area) => `${area.code} from ${dateLabel(area.app_from)}`).join(", ")}.` : "";
}

export type ParityShow = "all" | "differ" | "open";

export interface ParityFilter {
  show: ParityShow;
  area: string;
  q: string;
}

export const NO_PARITY_FILTER: ParityFilter = { show: "all", area: "", q: "" };

/** The filter in the URL (`show`, `area`, `q`), so a reload keeps it. */
export function parityFilterOf(search: string): ParityFilter {
  const params = new URLSearchParams(search);
  const show = params.get("show");
  return { show: show === "differ" || show === "open" ? show : "all", area: params.get("area") ?? "", q: params.get("q") ?? "" };
}

export function withParityFilter(search: string, filter: ParityFilter): string {
  const params = new URLSearchParams(search);
  const put = (key: string, value: string, none: string) => (value === none ? params.delete(key) : params.set(key, value));
  put("show", filter.show, "all");
  put("area", filter.area, "");
  put("q", filter.q, "");
  const text = params.toString();
  return text ? `?${text}` : "";
}

export const SHOW_TEXT: Record<ParityShow, string> = {
  all: "All rows",
  differ: "Rows that do not agree",
  open: "Rows with no explanation",
};

export const isOpen = (row: ParityRow) => !row.agrees && row.note === null;

export function filterParity(rows: readonly ParityRow[], filter: ParityFilter): ParityRow[] {
  const words = filter.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    const text = `${row.number} ${row.name}`.toLowerCase();
    return (
      (filter.show === "all" || (filter.show === "differ" ? !row.agrees : isOpen(row))) &&
      (!filter.area || row.area === filter.area) &&
      words.every((word) => text.includes(word))
    );
  });
}

export const areasOf = (rows: readonly ParityRow[]) =>
  [...new Set(rows.map((row) => row.area).filter((area): area is string => Boolean(area)))].sort((a, b) =>
    a.localeCompare(b, "en-US", { numeric: true }),
  );

export const numberText = (value: number | null) => (value === null ? "–" : gallons(value));

export const flagsText = (side: ParitySide) => (side.flags.length ? side.flags.map((flag) => FLAG_TEXT[flag]).join(", ") : "None");

export const differenceText = (row: ParityRow) => (row.agrees ? "Agrees" : row.differences.map((key) => DIFFERENCE_TEXT[key] ?? key).join(", "));


/** The header line: the share that agrees now against the gate. The trend shows the nightly count. */
export function summaryText(summary: ParitySummary): string {
  if (summary.share === null) return "No account of the sheet to compare yet.";
  const gate = `${Math.round(summary.gate * 100)}%`;
  const verdict = summary.meets_gate ? `at or above the ${gate} gate` : `below the ${gate} gate`;
  return `Now ${percentText(summary.share)} of ${summary.rows.toLocaleString("en-US")} rows agree within ${Math.round(summary.tolerance * 100)}%, ${verdict}.`;
}

export const unexplainedText = (count: number) =>
  count === 0 ? "Every difference has an explanation." : count === 1 ? "1 difference has no explanation." : `${count.toLocaleString("en-US")} differences have no explanation.`;

/** The note text is required only for the reason Other. */
export const noteProblem = (reason: ParityReason, note: string) =>
  reason === "other" && !note.trim() ? "Say why in the note when the reason is Other." : note.length > NOTE_MAX ? `A note has ${NOTE_MAX} characters at most.` : null;

export const explainText = (count: number) => (count === 1 ? "Explain 1 row" : `Explain ${count.toLocaleString("en-US")} rows`);

export const PARITY_COLUMNS: SortColumn<ParityRow>[] = [
  { key: "account", label: "Account", value: (r) => r.number },
  { key: "area", label: "Area", value: (r) => r.area },
  { key: "result", label: "Result", value: (r) => (r.agrees ? "agrees" : isOpen(r) ? "open" : "explained"), order: ["open", "explained", "agrees"] },
  { key: "sheet_rate", label: "Rate, sheet", value: (r) => r.sheet.rate, first: "desc", num: true },
  { key: "app_rate", label: "Rate, app", value: (r) => r.app.rate, first: "desc", num: true },
  { key: "sheet_weeks", label: "Weeks to full, sheet", value: (r) => r.sheet.weeks_to_full, num: true },
  { key: "app_weeks", label: "Weeks to full, app", value: (r) => r.app.weeks_to_full, num: true },
  { key: "sheet_load", label: "Load, sheet", value: (r) => r.sheet.load, first: "desc", num: true },
  { key: "app_load", label: "Load, app", value: (r) => r.app.load, first: "desc", num: true },
  { key: "sheet_flags", label: "Flags, sheet", value: (r) => r.sheet.flags.join(" ") },
  { key: "app_flags", label: "Flags, app", value: (r) => r.app.flags.join(" ") },
  { key: "note", label: "Explanation", value: (r) => (r.note ? REASON_TEXT[r.note.reason] : null) },
];

export const UNEXPLAINED_NOTE =
  "Both sides are the projection of the app. The sheet side uses only the pickups of the sheet; the app side uses all the pickups and the fill estimate.";

const plural = (count: number, word: string) => `${count.toLocaleString("en-US")} ${word}${count === 1 ? "" : "s"}`;

const NUMBER_TEXT: Record<string, [string, (value: number) => string]> = {
  rate: ["Rate", (value) => `${gallons(value)} gal/wk`],
  weeks_to_full: ["Weeks to full", (value) => `${gallons(value)} weeks`],
  load: ["Load at the next visit", (value) => `${gallons(value)} gal`],
};

/** Rule T-19: what the side that marks a flag expects of the container. */
const FLAG_EXPECTS: Record<ParityFlag, [string, string]> = {
  high_risk: ["High risk (early pull)", "expects this container to need a pickup before its next visit"],
  overflow: ["Overflow", "expects the load at the next visit to fill this container"],
  due: ["Due", "expects this container to be full in less than one week"],
};

/** "The app counts 3 pickups (240 gal) that the sheet does not have." and the same for the sheet. */
export function onlyText(detail: Pick<ParityDetail, "only">): string {
  const { app, sheet } = detail.only;
  const parts: string[] = [];
  if (app.count) parts.push(`The app counts ${plural(app.count, "pickup")} (${gallons(app.gal)} gal) that the sheet does not have.`);
  if (sheet.count) parts.push(`The sheet has ${plural(sheet.count, "pickup")} (${gallons(sheet.gal)} gal) that the app does not count.`);
  return parts.length ? parts.join(" ") : "Both sides count the same pickups.";
}

const dayText = (day: string | null) => (day ? dateLabel(day) : "none");

/** One plain sentence for each difference of the row; with the detail, the cause too. */
export function differenceSentences(row: ParityRow, detail?: ParityDetail | null): string[] {
  const inputs = detail?.inputs;
  let pickupsSaid = false;
  return row.differences.map((key) => {
    const number = NUMBER_TEXT[key];
    if (number) {
      const [label, format] = number;
      const value = (side: ParitySide) => {
        const found = side[key as "rate" | "weeks_to_full" | "load"];
        return found === null ? "nothing" : format(found);
      };
      const words = [`${label}: the sheet says ${value(row.sheet)}, the app says ${value(row.app)}.`];
      if (key === "weeks_to_full") words.push(`It is the container gallons (${gallons(row.capacity_gal)}) ÷ the rate.`);
      if (key === "load" && inputs) {
        if (inputs.sheet.next_visit !== inputs.app.next_visit)
          words.push(`The next visit is ${dayText(inputs.sheet.next_visit)} on the sheet side and ${dayText(inputs.app.next_visit)} on the app side.`);
        if (inputs.sheet.newest !== inputs.app.newest)
          words.push(`The newest pickup is ${dayText(inputs.sheet.newest)} on the sheet side and ${dayText(inputs.app.newest)} on the app side.`);
      }
      if (detail && !pickupsSaid && key !== "weeks_to_full") {
        words.push(onlyText(detail));
        pickupsSaid = true;
      }
      return words.join(" ");
    }
    const flag = key as ParityFlag;
    const sheetHas = row.sheet.flags.includes(flag);
    const [label, expects] = FLAG_EXPECTS[flag] ?? [key, "marks it"];
    const words = [`${label}: the ${sheetHas ? "sheet" : "app"} ${expects}; the ${sheetHas ? "app" : "sheet"} does not.`];
    if (flag === "high_risk" && inputs?.app.early_by === "estimate") words.push("The app finds it from the fill estimate at the next visit; the sheet finds it from the projected gallons.");
    return words.join(" ");
  });
}

export const ON_TEXT: Record<ParityPickup["on"], string> = { both: "Both", app: "App only", sheet: "Sheet only" };

export const pickupSourceText = (pickup: ParityPickup) => PICKUP_SOURCE_TEXT[pickup.source] ?? pickup.source;

/** The pickups that only one side counts, the newest first. */
export const onePickups = (detail: ParityDetail) => detail.pickups.filter((pickup) => pickup.on !== "both");

/** One decimal, half to even on the exact value, as Python `round(value, 1)` of the parity rows. */
export function roundOne(value: number): number {
  const exact = value.toFixed(20);
  const tenths = Math.floor(Math.abs(value) * 10 + 1e-9);
  const tie = /\.\d50*$/.test(exact);
  if (!tie) return Number(value.toFixed(1));
  return (Math.sign(value) * (tenths % 2 === 0 ? tenths : tenths + 1)) / 10;
}

/** "Nov 3, 2025 to Oct 27, 2026": the window of the pickups, with each year. */
export function windowText(start: string, end: string): string {
  const day = (date: string) => new Date(`${date.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  return `${day(start)} to ${day(end)}`;
}

const yesNo = (value: boolean | null) => (value === null ? "–" : value ? "Yes" : "No");
const numberOr = (value: number | null, unit = "") => (value === null ? "–" : `${gallons(roundOne(value))}${unit}`);
const shareOr = (value: number | null) => (value === null ? "–" : `${Math.round(value * 100)}%`);

/** The inputs of the two sides, each with its words and true when the two differ. */
export function inputRows(inputs: { app: ParityInputs; sheet: ParityInputs }): { label: string; sheet: string; app: string; differ: boolean }[] {
  const periods = Math.max(inputs.app.periods.length, inputs.sheet.periods.length);
  const rows: [string, (side: ParityInputs) => string][] = [
    ["Restart day", (side) => (side.restart ? dateLabel(side.restart) : "–")],
    ["Newest pickup", (side) => (side.newest ? dateLabel(side.newest) : "–")],
    [periods === 1 ? "Gallons of the last period of 13 weeks" : `Gallons of the last ${periods} periods of 13 weeks, newest first`, (side) => side.periods.map((gal) => gallons(gal)).join(" · ")],
    ["Stated rate", (side) => numberOr(side.stated_rate, " gal/wk")],
    ["Rate used", (side) => numberOr(side.rate_used, " gal/wk")],
    ["Weeks left", (side) => numberOr(side.weeks_left)],
    ["Next visit", (side) => (side.next_visit ? dateLabel(side.next_visit) : "–")],
    ["Gallons at the next visit", (side) => numberOr(side.projected_gal, " gal")],
    ["Share at the next visit", (side) => shareOr(side.projected_share)],
    ["Early pull", (side) => `${yesNo(side.early_pull)}${side.early_pull === null ? "" : side.early_by === "estimate" ? ", from the fill estimate" : ", from the projected gallons"}`],
  ];
  return rows.map(([label, words]) => {
    const sheet = words(inputs.sheet);
    const app = words(inputs.app);
    return { label, sheet, app, differ: sheet !== app };
  });
}
